/**
 * Thumbnail Cache Store
 *
 * Persists thumbnails to IndexedDB for offline access.
 * Uses Zustand for reactive state + Dexie for persistence.
 */

import { create } from 'zustand';
import { db } from '@/db/database';

interface ThumbnailState {
  // In-memory cache for fast lookups
  cache: Map<string, string>;
  isLoaded: boolean;

  // Load cache from IndexedDB
  loadCache: () => Promise<void>;

  // Get thumbnail (checks memory first, then DB)
  getThumbnail: (url: string) => string | undefined;

  // Cache thumbnail to both memory and DB
  cacheThumbnail: (url: string, data: string) => Promise<void>;

  // Check if thumbnail is cached
  hasThumbnail: (url: string) => boolean;
}

export const useThumbnailStore = create<ThumbnailState>((set, get) => ({
  cache: new Map(),
  isLoaded: false,

  loadCache: async () => {
    if (get().isLoaded) return;

    try {
      const entries = await db.thumbnailCache.toArray();
      const cache = new Map<string, string>();
      for (const entry of entries) {
        cache.set(entry.url, entry.data);
      }
      set({ cache, isLoaded: true });
      console.log(`[Thumbnails] Loaded ${cache.size} cached thumbnails from DB`);
    } catch (error) {
      console.error('[Thumbnails] Failed to load cache:', error);
      set({ isLoaded: true });
    }
  },

  getThumbnail: (url: string) => {
    return get().cache.get(url);
  },

  cacheThumbnail: async (url: string, data: string) => {
    // Update memory cache immediately
    set((state) => {
      const newCache = new Map(state.cache);
      newCache.set(url, data);
      return { cache: newCache };
    });

    // Persist to IndexedDB
    try {
      await db.thumbnailCache.put({
        url,
        data,
        cachedAt: Date.now(),
      });
    } catch (error) {
      console.error('[Thumbnails] Failed to cache:', error);
    }
  },

  hasThumbnail: (url: string) => {
    return get().cache.has(url);
  },
}));

// Auto-load cache on module init
useThumbnailStore.getState().loadCache();
